import React, { useContext } from "react";
import styled from "@emotion/styled";
import { getWelcomeBackUrl } from "@src/utils/getWelcomeBackUrl";
import HeaderLink from "../HeaderLink";
import Translate from "@src/components/translation/Translate";
import { Button } from "@web-monorepo/mds";
import { incrementMetric } from "@src/utils/logClient";
import { navigate } from "gatsby";
import * as S from "./styles";
import { AppDataContext } from "@src/components/AppDataContext";
import { mediaQueriesMax } from "@src/styles/theme";

const MobileButtonWrapper = styled.div`
  ${mediaQueriesMax[2]} {
    padding-inline: 15px;
  }
`;

type SignupOverride = {
  label: React.ReactNode;
  href: string;
  onClick: () => void;
};

const LoginPart = (props: {
  openLoginModal: () => void;
  openSignupModal: () => void;
  showBookCallInsteadOfSignup?: boolean;
  onBookCall?: () => void;
  context?: "default" | "onAccentDark";
  // Replaces the default "Sign up" CTA with a page-specific one (e.g. Sparks' "Free week").
  signupOverride?: SignupOverride;
}) => {
  const {
    openLoginModal,
    openSignupModal,
    showBookCallInsteadOfSignup,
    onBookCall,
    context = "default",
    signupOverride,
  } = props;
  const { data: appData } = useContext(AppDataContext);
  function openWelcomeBackLink() {
    incrementMetric("external_site.welcome_back_click");
    // @ts-expect-error Can be `undefined`
    const welcomeBackUrl = getWelcomeBackUrl(appData.type, appData.userData?.isChildAsParent);

    if (window) {
      window.location.href = welcomeBackUrl;
    }
  }
  let LoginPartRender: React.ReactNode = null;
  if (appData.loadingSession) {
    LoginPartRender = (
      <>
        <a href="https://teach.classdojo.com/">
          <S.LinkText css={{ paddingInline: "5px" }}>
            <Translate path="layouts.main.teacher_login" />
          </S.LinkText>
        </a>
        <a href="https://home.classdojo.com/">
          <S.LinkText css={{ paddingInline: "5px" }}>
            <Translate path="layouts.main.parent_login" />
          </S.LinkText>
        </a>
      </>
    );
  } else {
    if (appData.userData) {
      LoginPartRender = (
        <>
          <HeaderLink onClick={openWelcomeBackLink}>
            <S.LoggedInText>
              {appData.name ? (
                <Translate path="components.welcome_banner.hi" subs={{ name: appData.name }} />
              ) : (
                <Translate path="components.welcome_banner.welcome_back_anon" />
              )}
              <span>! </span>
              <Translate path="components.welcome_banner.come_in" />
            </S.LoggedInText>
          </HeaderLink>
          {showBookCallInsteadOfSignup && onBookCall && (
            <Button size="s" variant="primary" context={context} onClick={onBookCall} className="max-md:grow">
              <Translate path="pages.home.book_a_call" />
            </Button>
          )}
        </>
      );
    } else {
      LoginPartRender = (
        <>
          <Button
            onClick={openLoginModal}
            data-test-name="open-login-modal"
            size="s"
            variant="secondary"
            context={context}
            className="plausible-event-name=OpenLoginModal max-md:grow"
          >
            <Translate path="pages.home.log_in" />
          </Button>
          {showBookCallInsteadOfSignup && onBookCall ? (
            <Button
              size="s"
              variant="primary"
              context={context}
              onClick={onBookCall}
              data-test-name="open-book-call-modal"
              className="max-md:grow"
            >
              <Translate path="pages.home.book_a_call" />
            </Button>
          ) : signupOverride ? (
            <Button
              as="a"
              href={signupOverride.href}
              size="s"
              variant="primary"
              context={context}
              onClick={signupOverride.onClick}
              data-test-name="open-signup-cta"
              className="max-md:grow"
            >
              {signupOverride.label}
            </Button>
          ) : (
            <Button
              size="s"
              variant="primary"
              context={context}
              onClick={openSignupModal}
              data-test-name="open-signup-modal"
              className="plausible-event-name=OpenSignupModal max-md:grow"
            >
              <Translate path="pages.home.sign_up" />
            </Button>
          )}
        </>
      );
    }
  }

  return <>{LoginPartRender}</>;
};

export default LoginPart;
